import type { SearchableSelectOption } from '@/components/ui/SearchableSelect'
import type { SalesTeamOption } from '../types/api'
import type {
  ConfiguredReportConfiguredReportConfig,
  ConfiguredReportSelectFilterKind,
} from '../types/pages'
import * as filterOptions from '../constants/configuredReportFilterOptions'
import { currentMonthRange, currentYearRange, todayRange } from './configuredReportDateRanges'
import { dateForFormat } from './configuredReportFormatters'

export function createDefaultParams(
  config: ConfiguredReportConfiguredReportConfig,
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {
    page: 1,
    perPage: config.defaultPerPage ?? 15,
    sortBy: config.defaultSortBy ?? 'asc',
    orderBy: config.defaultOrderBy,
    q: '',
    ...(config.initialValues ?? {}),
  }
  if (config.dateFilter) {
    const range = config.dateFilter.defaultToToday
      ? todayRange()
      : config.dateFilter.defaultToYearStart
        ? currentYearRange()
        : config.dateFilter.defaultToCurrentMonth
          ? currentMonthRange()
          : { fromDt: '', toDt: '' }
    defaults[config.dateFilter.fromKey] = range.fromDt
    defaults[config.dateFilter.toKey] = range.toDt
  }
  for (const filter of config.selectFilters ?? [])
    defaults[filter.key] = defaults[filter.key] ?? (filter.multiple ? [] : '')
  for (const filter of config.textFilters ?? [])
    defaults[filter.key] = defaults[filter.key] ?? ''
  return defaults
}

export function buildParams(
  config: ConfiguredReportConfiguredReportConfig,
  params: Record<string, unknown>,
): URLSearchParams {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === '') continue
    if (key === 'q' && !config.search) continue
    if (Array.isArray(value)) {
      value.filter(Boolean).forEach((v) => qs.append(`${key}[]`, String(v)))
      continue
    }
    const isDateKey =
      config.dateFilter &&
      (key === config.dateFilter.fromKey || key === config.dateFilter.toKey)
    if (isDateKey) {
      if (config.dateFilter?.apiFormat === 'YMD') {
        qs.set(key, dateForFormat(value, 'YMD'))
        continue
      }
      const dmy = dateForFormat(value, 'DMY')
      if (dmy) qs.set(key, dmy)
      continue
    }
    qs.set(key, String(value))
  }
  return qs
}

export function buildExportParams(
  config: ConfiguredReportConfiguredReportConfig,
  params: Record<string, unknown>,
): URLSearchParams {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === '') continue
    if (key === 'q' && !config.search) continue
    if (Array.isArray(value)) {
      value
        .filter(Boolean)
        .forEach((v, index) => qs.set(`${key}[${index}]`, String(v)))
      continue
    }
    const isDateKey =
      config.dateFilter &&
      (key === config.dateFilter.fromKey || key === config.dateFilter.toKey)
    if (isDateKey) {
      const formatted =
        config.dateFilter?.exportFormat === 'YMD'
          ? dateForFormat(value, 'YMD')
          : dateForFormat(value, 'DMY')
      if (formatted) qs.set(key, formatted)
      continue
    }
    qs.set(key, String(value))
  }
  return qs
}

export function countActiveFilters(
  config: ConfiguredReportConfiguredReportConfig,
  params: Record<string, unknown>,
  defaults: Record<string, unknown>,
) {
  let count = 0
  const changed = (key: string) => {
    const value = params[key]
    const defaultValue = defaults[key]
    if (Array.isArray(value) || Array.isArray(defaultValue)) {
      return JSON.stringify(value ?? []) !== JSON.stringify(defaultValue ?? [])
    }
    return String(value ?? '') !== String(defaultValue ?? '')
  }
  if (config.search && String(params.q ?? '').trim() && changed('q')) count++
  if (
    config.dateFilter &&
    (changed(config.dateFilter.fromKey) || changed(config.dateFilter.toKey))
  ) {
    count++
  }
  for (const filter of config.selectFilters ?? []) {
    if (changed(filter.key)) count++
  }
  for (const filter of config.textFilters ?? []) {
    if (String(params[filter.key] ?? '').trim() && changed(filter.key)) count++
  }
  return count
}

export function selectOptionsForKind(
  kind: ConfiguredReportSelectFilterKind,
  companies: SearchableSelectOption[],
  properties: SearchableSelectOption[],
  salesTeams: SalesTeamOption[],
): SearchableSelectOption[] {
  if (kind === 'company') return companies
  if (kind === 'property') return properties
  if (kind === 'salesTeam') {
    return salesTeams.map((team) => ({
      value: String(team.sales_team_id),
      label: team.team_name,
    }))
  }
  if (kind === 'paymentMethod') return filterOptions.PAYMENT_METHOD_OPTIONS
  if (kind === 'reportType') return filterOptions.REPORT_TYPE_OPTIONS
  if (kind === 'source') return filterOptions.SOURCE_OPTIONS
  if (kind === 'chargebackStatus') return filterOptions.CHARGEBACK_STATUS_OPTIONS
  if (kind === 'pmc') return filterOptions.PMC_OPTIONS
  if (kind === 'roleName') return filterOptions.ROLE_NAME_OPTIONS
  if (kind === 'listed') return filterOptions.LISTED_STATUS_OPTIONS
  if (kind === 'uploaded') return filterOptions.UPLOADED_OPTIONS
  if (kind === 'lastLogin') return filterOptions.LAST_LOGIN_OPTIONS
  if (kind === 'specialNote') return filterOptions.SPECIAL_NOTE_OPTIONS
  if (kind === 'device') return filterOptions.DEVICE_OPTIONS
  if (kind === 'actionKey') return filterOptions.ACTION_KEY_OPTIONS
  if (kind === 'failedLoginType') return filterOptions.FAILED_LOGIN_TYPE_OPTIONS
  if (kind === 'rentalStatus') return filterOptions.RENTAL_STATUS_OPTIONS
  if (kind === 'defaultStatus') return filterOptions.DEFAULT_STATUS_OPTIONS
  if (kind === 'claimStatus') return filterOptions.CLAIM_STATUS_OPTIONS
  if (kind === 'lockStatus') return filterOptions.LOCK_STATUS_OPTIONS
  return filterOptions.PROPERTY_STATUS_OPTIONS
}
